// Approvals inbox (Phase 0.1) — a unified, cross-type queue of requests awaiting
// the signed-in user's decision, backed by the approval-workflow engine. Shared
// by HR (admin nav) and managers (team nav). Inline-actable types (leave) get
// Approve/Reject + bulk; types that need extra input (OKR needs a rating) link
// to their module screen instead.
import { useState, useEffect, useMemo } from 'react';
import { Drawer, TextField, IconButton } from '@mui/material';
import { Inbox, Check, X, AlertTriangle, RefreshCw, CalendarOff, FileCheck } from 'lucide-react';
import { Pill, Card, SectionTitle, CenterSpinner, PrimaryBtn, OutlineBtn, WidgetGrid, KPI } from './ui';
import { EmptyState, UserAvatar } from './components';
import { AG } from './theme/tokens';
import { api } from './api';

const STATE_COLOR = {
  Active: AG.warning,
  Approved: AG.success,
  Rejected: AG.error,
  Skipped: AG.n400,
  Pending: AG.n300,
  Escalated: AG.purple,
};
const typeLabel = (k) => (k === 'leave' ? 'Leave' : k === 'okr' ? 'OKR' : k);
const typeTone = (k) => (k === 'leave' ? 'info' : k === 'okr' ? 'primary' : 'neutral');

function dueText(slaDueAt) {
  if (!slaDueAt) return null;
  return new Date(slaDueAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export default function ApprovalsInbox({ showToast, onNavigate }) {
  const [items, setItems] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [detailId, setDetailId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () =>
    api
      .approvalsInbox()
      .then((rows) => setItems(rows))
      .catch((e) => {
        showToast(e.message);
        setItems([]);
      });
  useEffect(() => {
    load();
  }, []);

  const overdue = useMemo(() => (items || []).filter((i) => i.overdue).length, [items]);
  const leaveCount = useMemo(() => (items || []).filter((i) => i.workflowKey === 'leave').length, [items]);
  const bulkable = useMemo(() => (items || []).filter((i) => i.inlineActable && i.allowBulk), [items]);

  const toggleSel = (id) =>
    setSelected((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const act = async (id, decision, comment) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.actApproval(id, decision, comment);
      showToast(`Request ${decision.toLowerCase()}`);
      setDetailId(null);
      setSelected((s) => {
        const n = new Set(s);
        n.delete(id);
        return n;
      });
      await load();
    } catch (e) {
      showToast(e.message);
    } finally {
      setBusy(false);
    }
  };

  const bulkAct = async (decision) => {
    const ids = [...selected];
    if (!ids.length || busy) return;
    setBusy(true);
    try {
      const r = await api.bulkActApprovals(ids, decision);
      showToast(`${r.approved} ${decision.toLowerCase()}${r.failed ? `, ${r.failed} failed` : ''}`);
      setSelected(new Set());
      await load();
    } catch (e) {
      showToast(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!items) return <CenterSpinner />;

  return (
    <>
      <div className="flex items-center justify-between" style={{ marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
        <SectionTitle title="Approvals" sub="Requests awaiting your decision" />
        <OutlineBtn onClick={load}>
          <RefreshCw size={14} style={{ marginRight: 6 }} /> Refresh
        </OutlineBtn>
      </div>

      <div style={{ marginBottom: 20 }}>
        <WidgetGrid>
          <KPI label="Awaiting you" value={items.length} icon={Inbox} iconTone="primary" />
          <KPI label="Overdue" value={overdue} icon={AlertTriangle} iconTone={overdue ? 'warning' : 'success'} />
          <KPI label="Leave requests" value={leaveCount} icon={CalendarOff} iconTone="info" />
        </WidgetGrid>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div
          className="flex items-center justify-between"
          style={{ marginBottom: 14, padding: '10px 14px', background: AG.primaryLightest, border: `1px solid ${AG.n200}`, borderRadius: 12, gap: 12, flexWrap: 'wrap' }}
        >
          <span style={{ fontSize: 13, fontWeight: 600, color: AG.text }}>{selected.size} selected</span>
          <div className="flex items-center gap-2">
            <OutlineBtn onClick={() => bulkAct('Rejected')} disabled={busy}>
              <X size={14} style={{ marginRight: 6 }} /> Reject
            </OutlineBtn>
            <PrimaryBtn onClick={() => bulkAct('Approved')} disabled={busy}>
              <Check size={14} style={{ marginRight: 6 }} /> Approve all
            </PrimaryBtn>
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <Card>
          <EmptyState icon={FileCheck} title="You're all caught up" desc="No approvals are waiting on you right now." />
        </Card>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {items.map((it) => (
            <div
              key={it.requestId}
              style={{ background: '#fff', border: `1px solid ${it.overdue ? AG.warning : AG.n200}`, borderRadius: 14, padding: 16, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}
            >
              {it.inlineActable && it.allowBulk && (
                <input
                  type="checkbox"
                  checked={selected.has(it.requestId)}
                  onChange={() => toggleSel(it.requestId)}
                  style={{ width: 16, height: 16, accentColor: AG.primary, cursor: 'pointer', flexShrink: 0 }}
                  aria-label={`Select ${it.title}`}
                />
              )}

              <UserAvatar name={it.requesterName} size="sm" />

              <button
                type="button"
                onClick={() => setDetailId(it.requestId)}
                style={{ flex: 1, minWidth: 220, textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}
              >
                <div style={{ fontWeight: 600, fontSize: 14, color: AG.text }}>{it.title}</div>
                <div className="flex items-center gap-2" style={{ marginTop: 5, flexWrap: 'wrap' }}>
                  <Pill tone={typeTone(it.workflowKey)}>{typeLabel(it.workflowKey)}</Pill>
                  <span style={{ fontSize: 12, color: AG.n500 }}>{it.stepName}</span>
                  {it.requesterDept && <span style={{ fontSize: 12, color: AG.n400 }}>· {it.requesterDept}</span>}
                </div>
              </button>

              <div className="flex items-center gap-2" style={{ flexShrink: 0 }}>
                {it.overdue ? (
                  <Pill tone="error" dot>Overdue</Pill>
                ) : (
                  it.slaDueAt && <span style={{ fontSize: 12, color: AG.n500 }}>Due {dueText(it.slaDueAt)}</span>
                )}
                {it.inlineActable ? (
                  <>
                    <OutlineBtn onClick={() => act(it.requestId, 'Rejected')} disabled={busy}>
                      <X size={14} />
                    </OutlineBtn>
                    <PrimaryBtn onClick={() => act(it.requestId, 'Approved')} disabled={busy}>
                      <Check size={14} style={{ marginRight: 6 }} /> Approve
                    </PrimaryBtn>
                  </>
                ) : onNavigate ? (
                  <OutlineBtn onClick={() => onNavigate(it)}>Review</OutlineBtn>
                ) : (
                  <Pill tone="neutral">Review in Performance</Pill>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <ApprovalDetail id={detailId} onClose={() => setDetailId(null)} onAct={act} busy={busy} />
    </>
  );
}

// Right-side drawer: the step timeline + action history, with a commented
// Approve/Reject for inline-actable requests.
function ApprovalDetail({ id, onClose, onAct, busy }) {
  const [data, setData] = useState(null);
  const [comment, setComment] = useState('');

  useEffect(() => {
    if (!id) {
      setData(null);
      setComment('');
      return;
    }
    setData(null);
    api.approval(id).then(setData).catch(() => setData(null));
  }, [id]);

  return (
    <Drawer anchor="right" open={!!id} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 420 } } }}>
      <div style={{ padding: 20, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div className="flex items-center justify-between" style={{ marginBottom: 4 }}>
          <span style={{ fontWeight: 700, fontSize: 16, color: AG.text }}>Approval</span>
          <IconButton size="small" onClick={onClose} aria-label="Close"><X size={18} /></IconButton>
        </div>

        {!data ? (
          <CenterSpinner />
        ) : (
          <>
            <div style={{ fontSize: 14, color: AG.text, fontWeight: 600 }}>{data.title}</div>
            <div className="flex items-center gap-2" style={{ marginTop: 6 }}>
              <Pill tone={typeTone(data.workflowKey)}>{typeLabel(data.workflowKey)}</Pill>
              <Pill tone={data.status === 'Approved' ? 'success' : data.status === 'Rejected' ? 'error' : 'warning'}>{data.status}</Pill>
            </div>

            {/* Step timeline */}
            <div style={{ marginTop: 18, fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: AG.n400 }}>Steps</div>
            <div style={{ marginTop: 8, display: 'grid', gap: 8 }}>
              {data.steps.map((s) => (
                <div key={s.order} className="flex items-start gap-2" style={{ padding: '8px 10px', border: `1px solid ${AG.n200}`, borderRadius: 10 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 99, background: STATE_COLOR[s.state] || AG.n300, marginTop: 5, flexShrink: 0 }} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div className="flex items-center justify-between" style={{ gap: 8 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: AG.text }}>{s.name}</span>
                      <span style={{ fontSize: 12, color: STATE_COLOR[s.state] || AG.n500, fontWeight: 600 }}>{s.state}</span>
                    </div>
                    {s.approverName && <div style={{ fontSize: 12, color: AG.n500 }}>{s.approverName}</div>}
                    {s.comment && <div style={{ fontSize: 12, color: AG.n600, marginTop: 3, fontStyle: 'italic' }}>“{s.comment}”</div>}
                  </div>
                </div>
              ))}
            </div>

            {/* Action history */}
            {data.actions?.length > 0 && (
              <>
                <div style={{ marginTop: 18, fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: AG.n400 }}>History</div>
                <div style={{ marginTop: 8, display: 'grid', gap: 6 }}>
                  {data.actions.map((a, i) => (
                    <div key={i} style={{ fontSize: 12, color: AG.n600 }}>
                      <span style={{ fontWeight: 600, color: AG.text }}>{a.actorName}</span> · {a.action.toLowerCase().replace(/_/g, ' ')}
                      {a.comment ? ` — “${a.comment}”` : ''}
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Inline decision */}
            {data.inlineActable && ['Pending', 'Objected'].includes(data.status) && (
              <div style={{ marginTop: 'auto', paddingTop: 18 }}>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  minRows={2}
                  placeholder="Comment (optional)"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                />
                <div className="flex items-center gap-2" style={{ marginTop: 10, justifyContent: 'flex-end' }}>
                  <OutlineBtn onClick={() => onAct(data.id, 'Rejected', comment)} disabled={busy}>
                    <X size={14} style={{ marginRight: 6 }} /> Reject
                  </OutlineBtn>
                  <PrimaryBtn onClick={() => onAct(data.id, 'Approved', comment)} disabled={busy}>
                    <Check size={14} style={{ marginRight: 6 }} /> Approve
                  </PrimaryBtn>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Drawer>
  );
}
